import React, { useCallback, useEffect } from "react";

import { useWarehouseContext } from "./WarehouseProvider";

import { useStepsWithData } from "./useStepsWithData";

import { ReactComponent as WarningSvg } from "./svgs/warning.svg";
import { ReactComponent as ArrowSvg } from "./svgs/arrow.svg";
import { ReactComponent as InfoSvg } from "./svgs/info.svg";
import { Header } from "./components/Header";
import { FullscreenModal } from "./components/FullscreenModal";
import { GotoAllStepsButton } from "./components/GotoAllStepsButton";
import { usePluralizer } from "./utils/usePluralizer";
import { useLocale } from "./useLocale";
import {
  WarehouseButtonsFooter,
  WarehouseButton,
  WarehouseButtonGroup,
} from "./components/WarehouseButtonsFooter";

import { useSteps } from "./useSteps";

export const ProcessingIssues = () => {
  const { updateTakeback, clearTakeback, confirmWithPin } =
    useWarehouseContext();

  const { jumpTo, exit, previous } = useSteps();

  const stepsData = useStepsWithData();

  const locale = useLocale();
  const pluralizer = usePluralizer(locale);

  const onFinish = useCallback(
    () =>
      confirmWithPin(() => {
        updateTakeback();
        clearTakeback();
        exit({ successMessage: "Successfully processed package" });
      }),
    [clearTakeback, confirmWithPin, exit, updateTakeback]
  );

  const incompleteSteps = stepsData.filter(
    (step) => !step.completed && step.visible
  );
  const errors = stepsData.filter((step) => step.error && step.visible);

  // Handle Enter key to trigger Finish button
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onFinish();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onFinish]);

  return (
    <FullscreenModal>
      <div className="flex flex-col h-full">
        <Header showModal={true}>
          <div className="flex justify-between items-center w-full">
            <div className="w-10"></div>
            <div className="flex flex-col items-center gap-1">
              <span className="font-bold text-2xl text-white">
                Processing Issues
              </span>
              <span className="text-sm text-[#8086A0]">
                Please review all issues before proceeding, or enter a pin to
                finish anyway.
              </span>
            </div>
            <GotoAllStepsButton />
          </div>
        </Header>
        <div className="flex-1 overflow-y-auto">
          <div className="w-full p-8 flex flex-col gap-3">
            <div className="flex flex-col justify-center items-center">
              {incompleteSteps.length > 0 && (
                <>
                  <h2 className="text-[18px] font-bold mr-auto text-white text-left pb-4 pt-6">
                    {pluralizer(incompleteSteps.length, "Incomplete Step")}
                  </h2>
                  <ul className="w-full flex flex-col gap-4 mt-x-large">
                    {incompleteSteps.map(
                      ({ StepComponent, details }, index) => {
                        return (
                          <li
                            key={`incomplete-step-${index}`}
                            className="flex flex-row items-center justify-center text-xl pb-4 border-b border-b-white/10"
                          >
                            <WarningSvg style={{ width: "4rem" }} />
                            <span className="flex flex-col gap-1 justify-center w-full ml-6">
                              <b className="text-white text-[16px]">
                                {StepComponent.title}
                              </b>
                              <p className="text-sm text-[#8086A0] text-[14px]">
                                {details}
                              </p>
                            </span>
                            <button
                              className="border-none flex flex-row gap-2 items-center w-full justify-end text-[#2256DB] text-xl"
                              onClick={() => jumpTo(StepComponent)}
                            >
                              Complete Step <ArrowSvg />
                            </button>
                          </li>
                        );
                      }
                    )}
                  </ul>
                </>
              )}

              {errors.length > 0 && (
                <>
                  <h2 className="text-[18px] font-bold mr-auto text-white text-left pb-4 pt-6">
                    {pluralizer(errors.length, "Error")}
                  </h2>
                  <ul className="w-full flex flex-col gap-4 mt-x-large">
                    {errors.map(
                      ({ StepComponent, title, details, linkText }) => {
                        return (
                          <li
                            key={title}
                            className="flex flex-row items-center pb-4 border-b border-b-white/10"
                          >
                            <InfoSvg
                              style={{ width: "3rem", fill: "#C12960" }}
                            />
                            <span className="flex flex-col gap-1 w-full ml-6">
                              <b className="text-white text-[16px]">{title}</b>
                              <p className="text-sm text-[#8086A0] text-[14px]">
                                {details}
                              </p>
                            </span>
                            <button
                              className="border-none flex flex-row gap-2 items-center justify-end text-[#2256DB] text-xl"
                              onClick={() => jumpTo(StepComponent)}
                            >
                              {linkText} <ArrowSvg />
                            </button>
                          </li>
                        );
                      }
                    )}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>

        <WarehouseButtonsFooter>
          <WarehouseButtonGroup>
            <WarehouseButton variant="default" onClick={previous}>
              Back
            </WarehouseButton>
            <WarehouseButton variant="primary" onClick={onFinish}>
              Finish
            </WarehouseButton>
          </WarehouseButtonGroup>
        </WarehouseButtonsFooter>
      </div>
    </FullscreenModal>
  );
};
